import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import {
  AppointmentStatusCard,
  type BookedAppointment,
} from '@/modules/book-appointment';
import React from 'react';
import { Modal, Pressable, StyleSheet, View } from 'react-native';
import { usePropertyLabels } from '../hooks/usePropertyLabels';

export interface UnitNextStepSheetProps {
  visible: boolean;
  onClose: () => void;
  /** Required — sheet only shows the booked appointment card. */
  appointment: BookedAppointment;
  onCancelAppointment?: () => void;
  onRescheduleAppointment?: () => void;
}

/**
 * Mobile bottom sheet after booking: Appointment Confirmed details only
 * (Cancel / Reschedule). Replaces the old “Choose Your Next Step” dual-card sheet.
 */
export function UnitNextStepSheet({
  visible,
  onClose,
  appointment,
  onCancelAppointment,
  onRescheduleAppointment,
}: Readonly<UnitNextStepSheetProps>) {
  const colors = useUIColors();
  const { UNIT_DETAILS: labels } = usePropertyLabels();

  React.useEffect(() => {
    if (visible) {
      trackCatalogEvent({
        screen: ALMETLAA_SCREEN.RESERVE,
        actionName: ALMETLAA_ACTION.RESERVE.UNIT_NEXT_STEP_SHEET_OPEN,
        eventType: 'view',
      });
    }
  }, [visible]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View
        style={[styles.overlay, { backgroundColor: colors.modalBackdrop }]}
      >
        <Pressable
          style={styles.backdrop}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={labels.CLOSE}
        />
        <View
          style={[styles.sheet, { backgroundColor: colors.backgroundLevel1 }]}
        >
          <View style={styles.handleRow}>
            <View
              style={[
                styles.handle,
                { backgroundColor: colors.outlineDisabled },
              ]}
            />
          </View>

          <View style={styles.content}>
            <AppointmentStatusCard
              variant="sheet"
              appointment={appointment}
              onCancel={() => {
                onClose();
                onCancelAppointment?.();
              }}
              onReschedule={() => {
                onClose();
                onRescheduleAppointment?.();
              }}
              iconBackgroundColor={colors.appointmentIconBg}
              iconColor={colors.appointmentIconTint}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  sheet: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '90%',
  },
  handleRow: {
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 14,
  },
  handle: {
    width: 60,
    height: 4,
    borderRadius: 4,
  },
  content: {
    paddingHorizontal: 20,
    paddingTop: 0,
    paddingBottom: 24,
  },
});

export default UnitNextStepSheet;
